import fetch from "auth/FetchInterceptor";
import store from "redux/store";
import { UPDATE_NOTIFICATIONS } from "redux/constants/Menu";
import { UPDATE_PERMISSOES } from "redux/constants/Permissoes";
import { UPDATE_USER } from "redux/constants/User";

const MenuService = {};

export const SELECTED_TENANT_STORAGE_KEY = "SELECTED_TENANT_SCHEMA";

function getActiveTenants(tenants) {
	return (tenants || []).filter((tenant) => tenant.ativo !== false);
}

function findTenantBySchema(tenants, schema) {
	if (!schema) {
		return null;
	}
	const normalized = schema.toLowerCase();
	return (
		tenants.find(
			(tenant) => tenant.nome_schema?.toLowerCase() === normalized,
		) || null
	);
}

MenuService.clearStoredTenant = function () {
	localStorage.removeItem(SELECTED_TENANT_STORAGE_KEY);
};

MenuService.fetchUsuarioContexto = function () {
	return fetch({
		url: "/v1/rpc/usuario-contexto",
		method: "get",
	});
};

/** Dados do usuário e tenants — permissões efetivas só após escolher o tenant. */
MenuService.populateMenuBase = function (ctx) {
	const tenants = getActiveTenants(ctx?.usuario?.tenants);

	store.dispatch({
		type: UPDATE_USER,
		name: ctx?.usuario?.nome,
		role: "Usuário",
		grupo: ctx?.usuario?.grupo_usuario || null,
		tenants,
		avisosModulos: [],
	});

	if (ctx?.permissoes && Object.keys(ctx.permissoes).length > 0) {
		store.dispatch({
			type: UPDATE_PERMISSOES,
			permissoes: ctx.permissoes,
		});
	}

	store.dispatch({
		type: UPDATE_NOTIFICATIONS,
		empresasComErros: 0,
	});
};

MenuService.requestTenantSelection = function (tenants) {
	store.dispatch({
		type: UPDATE_USER,
		tenants: getActiveTenants(tenants),
		selectedTenant: null,
		awaitingTenantSelection: true,
		avisosModulos: [],
	});
};

/** Permissões, avisos de módulo e notificações do tenant selecionado. */
MenuService.loadTenantMenuData = async function () {
	try {
		const menuData = await MenuService.getMenuData();

		store.dispatch({
			type: UPDATE_PERMISSOES,
			permissoes: menuData?.permissoes || {},
		});

		store.dispatch({
			type: UPDATE_USER,
			grupo: menuData?.usuario?.grupo_usuario || null,
			avisosModulos: menuData?.avisos_modulos || [],
		});

		store.dispatch({
			type: UPDATE_NOTIFICATIONS,
			empresasComErros:
				menuData?.notificacoes?.total_empresas_com_erros ?? 0,
		});
	} catch {
		store.dispatch({
			type: UPDATE_USER,
			avisosModulos: [],
		});
		store.dispatch({
			type: UPDATE_NOTIFICATIONS,
			empresasComErros: 0,
		});
	}
};

MenuService.loadTenantNotifications = async function () {
	return MenuService.loadTenantMenuData();
};

MenuService.applyTenantAndLoadMenu = async function (tenantSchema) {
	if (!tenantSchema) {
		throw new Error("Empresa não selecionada");
	}

	localStorage.setItem(SELECTED_TENANT_STORAGE_KEY, tenantSchema);
	store.dispatch({
		type: UPDATE_USER,
		selectedTenant: tenantSchema,
		awaitingTenantSelection: false,
	});

	await MenuService.loadTenantMenuData();
};

/**
 * Após login ou tenant inválido: carrega menu/permissões e resolve empresa.
 * @returns {Promise<boolean>} true se já há tenant aplicado; false se aguarda seleção
 */
MenuService.bootstrapSession = async function (options = {}) {
	const { forceSelection = false, preserveStored = false } = options;

	if (!preserveStored) {
		MenuService.clearStoredTenant();
	}

	const ctx = await MenuService.fetchUsuarioContexto();
	const tenants = getActiveTenants(ctx?.usuario?.tenants);

	MenuService.populateMenuBase(ctx);

	if (tenants.length === 0) {
		throw new Error("Você não possui acesso a nenhuma empresa.");
	}

	if (forceSelection && tenants.length > 1) {
		MenuService.requestTenantSelection(tenants);
		return false;
	}

	const stored = localStorage.getItem(SELECTED_TENANT_STORAGE_KEY);
	const found = findTenantBySchema(tenants, stored);

	if (!found && tenants.length > 1) {
		MenuService.requestTenantSelection(tenants);
		return false;
	}

	const schema = (found || tenants[0]).nome_schema;
	await MenuService.applyTenantAndLoadMenu(schema);
	return true;
};

MenuService.handleTenantAccessError = async function () {
	try {
		await MenuService.bootstrapSession({ forceSelection: true });
		return true;
	} catch {
		return false;
	}
};

/*
    Recarrega menu completo (usuário + permissões + notificações) quando já há tenant.
*/
MenuService.populateMenuData = async function () {
	const stored = localStorage.getItem(SELECTED_TENANT_STORAGE_KEY);

	if (!stored) {
		return MenuService.bootstrapSession({ preserveStored: false });
	}

	const ctx = await MenuService.fetchUsuarioContexto();
	MenuService.populateMenuBase(ctx);
	await MenuService.applyTenantAndLoadMenu(stored);
};

MenuService.getMenuData = function () {
	return fetch({
		url: "/v1/rpc/menu",
		method: "get",
	});
};

export default MenuService;
